import React, { useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../../config/firebase";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    setError("");

    if (!email.trim() || !password.trim()) {
      setError("Veuillez remplir tous les champs");
      return;
    }

    if (
      email !== process.env.REACT_APP_AUTH_EMAIL ||
      password !== process.env.REACT_APP_AUTH_PASSWORD
    ) {
      setError("Identifiants incorrects");
      return;
    }

    setIsLoading(true);

    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      setError("Une erreur est survenue. Veuillez réessayer");
      console.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel - branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1A1A1A] relative overflow-hidden items-center justify-center">
        <div className="relative z-10 px-16 max-w-lg">
          {/* Logo */}
          <div className="mb-12">
            <img src="/logo.png" alt="Zzigs" className="w-16 h-16 rounded-xl object-cover" />
          </div>

          {/* Title */}
          <h1 className="text-4xl font-bold text-white leading-tight mb-4" style={{ fontFamily: "'DM Serif Display', serif" }}>
            Panneau<br />d'administration
          </h1>

          <p className="text-gray-500 text-sm leading-relaxed mb-12">
            Gérez vos utilisateurs, projets, offres et rendez-vous depuis un seul espace centralisé.
          </p>

          {/* Stats preview */}
          <div className="grid grid-cols-3 gap-6">
            <div>
              <div className="w-8 h-[2px] bg-[#E0BE71] mb-3" />
              <p className="text-[10px] uppercase tracking-widest text-gray-600 mb-1">Utilisateurs</p>
              <p className="text-white text-lg font-semibold">Gestion</p>
            </div>
            <div>
              <div className="w-8 h-[2px] bg-[#801111] mb-3" />
              <p className="text-[10px] uppercase tracking-widest text-gray-600 mb-1">Projets</p>
              <p className="text-white text-lg font-semibold">Suivi</p>
            </div>
            <div>
              <div className="w-8 h-[2px] bg-gray-600 mb-3" />
              <p className="text-[10px] uppercase tracking-widest text-gray-600 mb-1">Paiements</p>
              <p className="text-white text-lg font-semibold">Contrôle</p>
            </div>
          </div>
        </div>

        {/* Bottom branding */}
        <div className="absolute bottom-8 left-16">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gray-700">ZZIGS — Administration</p>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center bg-[#FAFAFA] px-6">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden mb-10 text-center">
            <img src="/logo.png" alt="Zzigs" className="w-12 h-12 rounded-lg object-cover mx-auto mb-4" />
            <p className="text-xs uppercase tracking-[0.2em] text-gray-400">Administration</p>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-[#1A1A1A] mb-1">
              Connexion
            </h2>
            <p className="text-sm text-gray-400">
              Accédez à votre espace de gestion
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-5 flex items-center gap-2.5 px-3.5 py-3 bg-red-50 border border-red-100 rounded-lg">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-red-500 flex-shrink-0">
                <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-8-5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 5Zm0 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd" />
              </svg>
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={login} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2" htmlFor="email">
                Email
              </label>
              <input
                id="email"
                autoFocus
                type="email"
                className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-[#1A1A1A] outline-none focus:border-[#801111] focus:ring-2 focus:ring-[#801111]/10 transition placeholder:text-gray-300 disabled:bg-gray-50 disabled:cursor-not-allowed"
                placeholder="admin@zzigs.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(""); }}
                disabled={isLoading}
                required
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2" htmlFor="password">
                Mot de passe
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  className="w-full px-3.5 py-2.5 pr-10 rounded-lg border border-gray-200 bg-white text-sm text-[#1A1A1A] outline-none focus:border-[#801111] focus:ring-2 focus:ring-[#801111]/10 transition placeholder:text-gray-300 disabled:bg-gray-50 disabled:cursor-not-allowed"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(""); }}
                  disabled={isLoading}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition outline-none"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                className="w-full py-2.5 rounded-lg text-sm font-medium text-white bg-[#1A1A1A] hover:bg-[#333] active:scale-[0.98] transition-all disabled:bg-gray-300 disabled:cursor-not-allowed disabled:active:scale-100 outline-none"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Connexion...
                  </span>
                ) : (
                  "Se connecter"
                )}
              </button>
            </div>
          </form>

          {/* Footer */}
          <div className="mt-10 pt-6 border-t border-gray-200/60">
            <p className="text-[10px] text-gray-400 text-center uppercase tracking-wider">
              © {new Date().getFullYear()} ZZIGS — Tous droits réservés
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
